import { useState, useEffect } from 'react';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  getCertificateSettings,
  updateCertificateSettings,
  updateLogo,
  uploadDirectorSignature,
} from '../../../../services/instanceService';
import { CertificateSettings } from '../../../../types/instanceTypes';

export const useCertificateSettings = () => {
  const { t } = useTranslation();
  const [settings, setSettings] = useState<CertificateSettings | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploadingSignature, setIsUploadingSignature] = useState(false);

  const fetchSettings = async () => {
    setIsLoading(true);
    try {
      const data = await getCertificateSettings();
      setSettings(data);
    } catch (err) {
      message.error(t('certificate.messages.loadError'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const handleSubmit = async (values: CertificateSettings) => {
    setIsSubmitting(true);
    try {
      await updateCertificateSettings(values);
      message.success(t('certificate.messages.updateSuccess'));
      await fetchSettings();
    } catch (err) {
      message.error(t('certificate.messages.updateError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSignatureUpload = async (file: File): Promise<string> => {
    setIsUploadingSignature(true);
    try {
      const signatureUrl = await uploadDirectorSignature(file);
      message.success(t('certificate.messages.signatureUploadSuccess'));
      return signatureUrl;
    } catch (err) {
      message.error(t('certificate.messages.signatureUploadError'));
      throw err;
    } finally {
      setIsUploadingSignature(false);
    }
  };

  const handleLogoUpload = async (file: File | null): Promise<string | boolean> => {
    setIsUploadingSignature(true);
    try {
      const logoUrl = await updateLogo(file);
      message.success(t('certificate.messages.logoUploadSuccess'));
      setSettings((prev) => {
        if (!prev) return prev;
        return { ...prev, logo: logoUrl };
      });
      return logoUrl;
    } catch (err) {
      return false;
    } finally {
      setIsUploadingSignature(false);
    }
  };

  return {
    settings,
    isLoading,
    isSubmitting,
    isUploadingSignature,
    handleSubmit,
    handleSignatureUpload,
    handleLogoUpload
  };
};
